import { apiClient } from '@/api/client';
import { z } from 'zod';
import { NotificationsData } from '@/types/display';
import { mapNotificationWireToDomain } from '../mappers/notification';
import {
    paginatedNotificationsWireSchema,
    unreadCountWireSchema,
    type PaginatedNotificationsWire,
    type UnreadCountWire
} from '../schemas/notification';

/**
 * Domain-oriented API module for notifications
 */

/**
 * Query parameters for listing notifications
 */
export interface ListNotificationsParams {
  page?: number;
  per_page?: number;
}

/**
 * Get user notifications
 * Returns paginated list of notifications for the authenticated user
 */
export async function getNotifications(
  params: ListNotificationsParams = {}
): Promise<NotificationsData> {
  const queryParams: string[] = [];
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/notifications${queryString}`;

  const wireData = await apiClient.get<PaginatedNotificationsWire>(endpoint);

  // Validate response against wire schema
  let validatedWire: PaginatedNotificationsWire;
  try {
    validatedWire = paginatedNotificationsWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Notifications data validation failed:', error.issues);
      const parseResult = paginatedNotificationsWireSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid notifications data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map wire format to domain format
  const notifications = validatedWire.items.map(mapNotificationWireToDomain);

  return { notifications };
}

/**
 * Get unread notifications count
 * Returns the number of unread notifications for the authenticated user
 */
export async function getUnreadCount(): Promise<number> {
  const endpoint = '/api/notifications/unread-count';
  const wireData = await apiClient.get<UnreadCountWire>(endpoint);

  // Validate response
  let validatedWire: UnreadCountWire;
  try {
    validatedWire = unreadCountWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Unread count validation failed:', error.issues);
      throw new Error('Invalid unread count data received from server');
    } else {
      throw error;
    }
  }

  return validatedWire.unread_count;
}

/**
 * Mark a single notification as read
 * @param id - Notification ID to mark as read
 */
export async function markAsRead(id: string): Promise<void> {
  const endpoint = `/api/notifications/${id}/read`;
  await apiClient.patch(endpoint, {});
}

/**
 * Mark all notifications as read for the authenticated user
 */
export async function markAllAsRead(): Promise<void> {
  const endpoint = '/api/notifications/mark-all-read';
  await apiClient.post(endpoint, {});
}
